import { Link, useNavigate, useSearch } from '@tanstack/react-router'
import { ctaLinkClasses } from '@workspace/ui/components/dsm/cta-button'
import {
  SecondaryButton,
  secondaryLinkClasses,
} from '@workspace/ui/components/dsm/secondary-button'
import { Lock, LockOpen, Pause, Play, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useInvestorContext } from '../../../../contexts/investor-guard'
import { useInvestorConfigQuery } from '../../../../hooks/api'
import { copy } from '../../copy/investor-copy'
import {
  useAddWatchdogCriterionMutation,
  useRemoveWatchdogCriterionMutation,
  useSetWatchdogCriterionLockedMutation,
  useSetWatchdogPausedMutation,
  useWatchdogQuery,
} from '../../hooks/use-investor-assistant'
import { formatShortDate } from '../following/dates'
import { StartupTile } from '../following/startup-tile'
import { cardLine } from './card-line'
import { isControlClick } from './row-click'

type Kind = 'STAGE' | 'SECTOR' | 'REGION' | 'PHRASE'
type Reason = { kind: Kind; value: string }
type Criterion = Reason & {
  id: string
  locked: boolean
  context: string | null
  /** Where it came from: the investor (MANUAL, FOCUS) or the watchdog's learning (SEARCH). */
  source: 'FOCUS' | 'SEARCH' | 'MANUAL'
}

/** How many matches the list shows before Show all. */
const SHOWN_MATCHES = 3

const PILL =
  'inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1 text-sm text-foreground'
const PILL_BUTTON =
  'cursor-pointer rounded-full text-muted-foreground outline-none transition-colors hover:text-foreground focus-visible:ring-2 focus-visible:ring-primary/50 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:text-muted-foreground'
/**
 * The Discovery watchdog tab: the focus the investor saved, what the watchdog learned from searches, and the startups
 * it found. The focus is the investor's locked criteria; "Edit focus" opens the firm's focus in Settings, as the
 * prototype does. Nothing here follows, passes or alerts anyone.
 */

export function WatchdogTab() {
  const [showAll, setShowAll] = useState(false)
  const navigate = useNavigate()
  // The startup whose side panel is open: its new match shows no Open button, since the panel is already open.
  const { open: openStartupId } = useSearch({ strict: false })
  const query = useWatchdogQuery()
  const config = useInvestorConfigQuery()
  const { investor } = useInvestorContext()
  const pause = useSetWatchdogPausedMutation()
  const add = useAddWatchdogCriterionMutation()
  const remove = useRemoveWatchdogCriterionMutation()
  const setLocked = useSetWatchdogCriterionLockedMutation()
  // The matches that were new when the tab opened stay in "New since your last visit" until the investor leaves it.
  const [fresh, setFresh] = useState<Set<string> | null>(null)
  useEffect(() => {
    if (query.data && fresh === null)
      setFresh(new Set(query.data.matches.filter((m) => m.seenAt === null).map((m) => m.startupId)))
  }, [query.data, fresh])
  // The last criterion removed, so a mistaken removal can be put back with one click.
  const [removed, setRemoved] = useState<{ kind: Kind; value: string; locked: boolean } | null>(
    null
  )
  const data = query.data
  if (!data) return <p className="text-sm text-muted-foreground">{copy('search.loading')}</p>

  const regionLabel = (code: string) =>
    config.data?.investmentRegions?.find((option) => option.value === code)?.label ?? code
  const reasonText = (reason: Reason) => {
    if (reason.kind === 'STAGE') {
      return copy(reason.value === 'preseed' ? 'search.criteria.preseed' : 'search.criteria.seed')
    }
    if (reason.kind === 'REGION') return regionLabel(reason.value)
    if (reason.kind === 'PHRASE') return copy('search.closest.phrase', { value: reason.value })
    return reason.value
  }
  /** A learned phrase reads with the regions its search named: “pilots” in Europe. */
  const criterionLabel = (criterion: Criterion) => {
    const base = reasonText(criterion)
    if (criterion.kind !== 'PHRASE' || !criterion.context) return base
    const regions = criterion.context.split(',').map(regionLabel).join(' or ')
    return `${base} ${copy('search.region.in', { region: regions })}`
  }
  const focus = data.criteria.filter((criterion) => criterion.locked)
  const learned = data.criteria.filter((criterion) => !criterion.locked)
  const newMatches = data.matches.filter((match) => fresh?.has(match.startupId) ?? false)
  const shownMatches = showAll ? data.matches : data.matches.slice(0, SHOWN_MATCHES)
  const openInSearch = (startupId: string) =>
    navigate({
      to: '/app/investor/$id/search',
      params: { id: investor.id },
      search: { tab: 'watchdog', open: startupId },
    })
  const failed = pause.isError || add.isError || remove.isError || setLocked.isError
  const criterionPill = (criterion: Criterion, { last }: { last: boolean }) => (
    <li key={criterion.id} className={PILL}>
      {criterionLabel(criterion)}
      {/* The lock moves a learned criterion into the focus and back. What the investor put in the focus
          (Settings, a saved search) is only removed, so it has no lock. */}
      {(!criterion.locked || criterion.source === 'SEARCH') && (
        <button
          type="button"
          aria-pressed={criterion.locked}
          aria-label={copy('watchdog.focus.lock', { what: reasonText(criterion) })}
          disabled={setLocked.isPending || last}
          onClick={() =>
            setLocked.mutate({
              path: { criterionId: criterion.id },
              body: { locked: !criterion.locked },
            })
          }
          className={PILL_BUTTON}
        >
          {criterion.locked ? (
            <Lock aria-hidden className="size-3.5" />
          ) : (
            <LockOpen aria-hidden className="size-3.5" />
          )}
        </button>
      )}
      <button
        type="button"
        aria-label={copy('watchdog.remove', { what: reasonText(criterion) })}
        disabled={remove.isPending || last}
        onClick={() =>
          remove.mutate(
            { path: { criterionId: criterion.id } },
            {
              onSuccess: () =>
                setRemoved({
                  kind: criterion.kind,
                  value: criterion.value,
                  locked: criterion.locked,
                }),
            }
          )
        }
        className={PILL_BUTTON}
      >
        <X aria-hidden className="size-3.5" />
      </button>
    </li>
  )

  return (
    <section aria-label={copy('watchdog.tab')} className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <span
            aria-hidden
            className={`size-2 rounded-full ${data.paused ? 'bg-muted-foreground' : 'bg-emerald-500'}`}
          />
          {data.paused ? copy('watchdog.paused') : copy('watchdog.on')}
        </p>
        <SecondaryButton
          type="button"
          disabled={pause.isPending}
          icon={data.paused ? <Play /> : <Pause />}
          onClick={() => pause.mutate({ body: { paused: !data.paused } })}
        >
          {copy(data.paused ? 'watchdog.resume' : 'watchdog.pause')}
        </SecondaryButton>
      </div>

      <div className="space-y-6 rounded-xl border border-border bg-card/30 p-4">
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              {copy('watchdog.focus.label')}
            </h3>
            <Link
              to="/app/investor/$id/settings"
              params={{ id: investor.id }}
              search={{ focus: true }}
              className="text-xs font-medium text-foreground underline-offset-2 hover:underline"
            >
              {copy('watchdog.focus.edit')}
            </Link>
          </div>
          <ul className="flex flex-wrap gap-2">
            {focus.map((criterion) => criterionPill(criterion, { last: focus.length === 1 }))}
          </ul>
          <p className="text-xs text-muted-foreground">{copy('watchdog.criteria.focus')}</p>
          {/* "Leave out" from Settings: changed there, through Edit focus, so these pills have no buttons. */}
          {data.leaveOut.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-muted-foreground">
                {copy('watchdog.leaveOut.label')}
              </span>
              <ul className="flex flex-wrap gap-2">
                {data.leaveOut.map((term) => (
                  <li key={term} className={`${PILL} text-muted-foreground`}>
                    {term}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="space-y-3 border-t border-border pt-6">
          <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            {copy('watchdog.learned.label')}
          </h3>
          {learned.length === 0 ? (
            <p className="text-sm text-muted-foreground">{copy('watchdog.learned.empty')}</p>
          ) : (
            <ul className="flex flex-wrap gap-2">
              {learned.map((criterion) => criterionPill(criterion, { last: false }))}
            </ul>
          )}
          <p className="text-xs text-muted-foreground">{copy('watchdog.learned.note')}</p>
        </div>
      </div>

      {newMatches.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            {copy('watchdog.new.heading', { n: newMatches.length })}
          </h3>
          <ul className="space-y-3">
            {newMatches.map((match) => (
              <li
                key={match.startupId}
                className="cursor-pointer rounded-xl border border-border bg-card/30 p-4 transition-colors hover:border-foreground/30"
                onClick={(event) => {
                  if (!isControlClick(event)) openInSearch(match.startupId)
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && event.target === event.currentTarget)
                    openInSearch(match.startupId)
                }}
              >
                <div className="flex items-start gap-3">
                  <StartupTile name={match.name} size="lg" />
                  <div className="min-w-0 flex-1">
                    <Link
                      to="/app/investor/$id/search"
                      params={{ id: investor.id }}
                      search={{ tab: 'watchdog', open: match.startupId }}
                      className="text-left text-sm font-semibold text-foreground hover:underline"
                    >
                      {match.name}
                    </Link>
                    {cardLine(match) !== '' && (
                      <p className="truncate text-xs text-muted-foreground">{cardLine(match)}</p>
                    )}
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <Link
                      to="/app/investor/$id/search"
                      params={{ id: investor.id }}
                      search={{ tab: 'watchdog', open: match.startupId, follow: true }}
                      className={ctaLinkClasses({ size: 'sm' })}
                    >
                      {copy('search.result.follow')}
                    </Link>
                    {match.startupId !== openStartupId && (
                      <Link
                        to="/app/investor/$id/search"
                        params={{ id: investor.id }}
                        search={{ tab: 'watchdog', open: match.startupId }}
                        className={secondaryLinkClasses({ size: 'sm' })}
                      >
                        {copy('search.result.open')}
                      </Link>
                    )}
                  </div>
                </div>
                <p className="mt-3 text-xs text-muted-foreground">
                  {reasonsSentence(match.reasons, reasonText)}
                </p>
                {match.passage && (
                  <div className="mt-2 space-y-2">
                    <blockquote className="rounded-lg bg-[#0F0F10] px-3 py-2.5 text-sm leading-relaxed text-foreground/80">
                      {'“'}
                      {match.passage.text}
                      {'”'}
                    </blockquote>
                    <span className="inline-flex items-center gap-1.5 rounded-[5px] border border-emerald-500/35 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wide text-emerald-500">
                      {match.passage.source}
                      {match.passage.date ? ` · ${match.passage.date}` : ''}
                    </span>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="space-y-3">
        <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          {copy('watchdog.all.heading', { n: data.matches.length })}
        </h3>
        {data.matches.length === 0 ? (
          <p className="text-sm text-muted-foreground">{copy('watchdog.matches.empty')}</p>
        ) : (
          <ul className="mt-4 divide-y divide-border rounded-xl border border-border bg-card/30">
            {shownMatches.map((match) => (
              <li
                key={match.startupId}
                className="flex cursor-pointer items-center gap-3 px-4 py-3 transition-colors hover:bg-white/[0.02]"
                onClick={() => openInSearch(match.startupId)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && event.target === event.currentTarget)
                    openInSearch(match.startupId)
                }}
              >
                <span
                  aria-hidden
                  className={`size-1.5 flex-none rounded-full ${match.seenAt === null ? 'bg-primary' : 'bg-transparent'}`}
                />
                <StartupTile name={match.name} />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-semibold text-foreground">
                    {match.name}
                  </span>
                  {match.oneLine && (
                    <span className="block truncate text-xs text-muted-foreground">
                      {match.oneLine}
                    </span>
                  )}
                </span>
                <span className="hidden w-56 shrink-0 truncate text-xs text-muted-foreground sm:block">
                  {copy('watchdog.all.matches', {
                    reasons: match.reasons.map(reasonText).join(' · '),
                  })}
                </span>
                <span className="w-24 shrink-0 text-right text-xs tabular-nums text-muted-foreground">
                  {copy('watchdog.all.joined', { date: formatShortDate(match.firstShownAt) })}
                </span>
              </li>
            ))}
          </ul>
        )}
        {data.matches.length > shownMatches.length && (
          <div className="flex items-center justify-between gap-3">
            <p className="text-xs text-muted-foreground">
              {copy('search.count', { shown: shownMatches.length, total: data.matches.length })}
            </p>
            <button
              type="button"
              onClick={() => setShowAll(true)}
              className="text-xs font-medium text-foreground underline-offset-2 hover:underline"
            >
              {copy('watchdog.showAll')}
            </button>
          </div>
        )}
      </div>

      {removed && (
        <p className="flex items-center gap-3 text-sm text-muted-foreground">
          {copy('watchdog.removed', { what: reasonText(removed) })}
          <button
            type="button"
            disabled={add.isPending}
            onClick={() =>
              add.mutate(
                { body: { kind: removed.kind, value: removed.value, locked: removed.locked } },
                { onSuccess: () => setRemoved(null) }
              )
            }
            className="text-xs font-medium text-foreground underline-offset-2 hover:underline"
          >
            {copy('watchdog.undo')}
          </button>
        </p>
      )}

      {failed && (
        <p role="alert" className="text-sm text-muted-foreground">
          {copy('watchdog.error')}
        </p>
      )}
      <p className="text-pretty text-xs text-muted-foreground">{copy('watchdog.footnote')}</p>
    </section>
  )
}

/** "Matches your focus (Fintech, Seed, Europe) and your interest in “paying customers”": focus reasons, then phrases. */
function reasonsSentence(reasons: Reason[], label: (reason: Reason) => string): string {
  const focus = reasons.filter((reason) => reason.kind !== 'PHRASE').map(label)
  const interest = reasons.filter((reason) => reason.kind === 'PHRASE').map(label)
  const focusPart =
    focus.length > 0 ? copy('watchdog.matches.focus', { reasons: focus.join(', ') }) : ''
  const interestPart =
    interest.length > 0
      ? copy('watchdog.matches.interest', { phrases: interest.join(' and ') })
      : ''
  if (focusPart && interestPart) return `${focusPart} and ${interestPart}`
  if (focusPart) return focusPart
  return copy('watchdog.matches.only', { what: interestPart })
}
